import { Box, Typography } from "@mui/material";

/**
 * The type and layout primitives inside an expanded discrepancy row, shared by
 * both row expanders and by the event history.
 *
 * Everything here is `body2`, the same size as the collapsed row above it. The
 * expanders were built at `caption` throughout, which made a finding's full
 * detail smaller than the summary it expands from.
 *
 * Their own module rather than living in `DiscrepancyRowExpanderShell`, which
 * renders the event history and would import from a file importing it back.
 */

/** A label/value pair for `DetailGrid`. Renders two grid cells, not a row wrapper. */
export function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      {/* component="span": a body2 Typography is a <p>, which cannot sit beside
          its value as a grid item without the pair breaking across rows. */}
      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }} color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="span" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </>
  );
}

/**
 * The label/value pairs on one two-column grid, so every value starts at the
 * same x whatever the labels say. A fixed `minWidth` on each label used to do
 * this, and "Uploaded Manifest Document" is wider than the width that was
 * picked, so that one value landed right of the two above it.
 */
export function DetailGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content minmax(0, 1fr)",
        columnGap: 3,
        rowGap: 0.5,
        // Only as wide as its content, so a divider under it spans the values
        // rather than the whole table.
        width: "fit-content",
        maxWidth: "100%",
      }}
    >
      {children}
    </Box>
  );
}

/** A small heading over a block inside an expander. */
export function DetailBlockLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      component="h6"
      color="text.secondary"
      sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}
    >
      {children}
    </Typography>
  );
}
